// Pure XP breakdown calculation — lifted from src/components/profile/XPBreakdown.tsx
// so both the classic XP Breakdown component and the Ultimate Team profile
// can share the same formula. Keep this in sync with calculate_player_xp_v2()
// in supabase/migrations/20251204_create_xp_v2_system.sql.

export interface XPBreakdownGame {
  sequence: number;
  status: string;
  unpaid?: boolean;
  /** A no_show an admin excused: no penalty. */
  excused?: boolean;
}

export interface XPBreakdownInput {
  gameHistory: XPBreakdownGame[];
  latestSequence: number;
  currentStreak: number;
  reserveXP?: number;
  reserveCount?: number;
  benchWarmerStreak?: number;
  registrationStreak?: number;
  registrationStreakApplies?: boolean;
  unpaidGames: number;
  shieldActive?: boolean;
  frozenStreakValue?: number | null;
  injuryTokenActive?: boolean;
  injuryReturnStreak?: number | null;
}

export interface XPBreakdownResult {
  baseXP: number;
  reserveXP: number;
  reserveCount: number;
  totalBaseXP: number;
  effectiveStreak: number;
  streakModifier: number;
  streakPercentage: number;
  reserveModifier: number;
  registrationModifier: number;
  unpaidGamesModifier: number;
  totalModifier: number;
  /** Unexcused no-shows inside the 40-game window. */
  noShowCount: number;
  /** XP taken off for them (50 each), after the clamp — can take finalXP below 0. */
  noShowPenalty: number;
  finalXP: number;
  isInjured: boolean;
  isShielded: boolean;
  injuryReturnBonusPercent: number | null;
}

export const NO_SHOW_XP_PENALTY = 50;

// v2 diminishing returns formula
// First 10 games: 10%, 9%, 8% … 1% (sum 55%)
// After 10: +1% per game
export function calculateStreakBonus(streak: number): number {
  if (streak <= 0) return 0;
  if (streak <= 10) {
    return (streak * 11 - (streak * (streak + 1)) / 2) / 100;
  }
  return (55 + (streak - 10)) / 100;
}

export function computeXPBreakdown(input: XPBreakdownInput): XPBreakdownResult {
  const {
    gameHistory,
    latestSequence,
    currentStreak,
    reserveXP = 0,
    reserveCount = 0,
    benchWarmerStreak = 0,
    registrationStreak = 0,
    registrationStreakApplies = false,
    unpaidGames,
    shieldActive = false,
    frozenStreakValue = null,
    injuryTokenActive = false,
    injuryReturnStreak = null,
  } = input;

  const baseXP = gameHistory.reduce((total, game) => {
    if (game.sequence > latestSequence || game.status === 'dropped_out' || game.status === 'no_show') return total;
    if (game.status === 'reserve') return total;
    const gamesAgo = latestSequence - game.sequence;
    return total + Math.max(1, 20 - gamesAgo * 0.5);
  }, 0);

  const totalBaseXP = baseXP + reserveXP;

  const isInjured = injuryTokenActive === true;
  const isShielded = shieldActive && frozenStreakValue != null;
  const effectiveStreak = isInjured
    ? 0
    : isShielded
      ? (frozenStreakValue as number)
      : currentStreak;

  const streakModifier = calculateStreakBonus(effectiveStreak);
  const streakPercentage = Math.round(streakModifier * 100);

  const reserveModifier = benchWarmerStreak ? benchWarmerStreak * 0.05 : 0;
  const registrationModifier =
    registrationStreak && registrationStreakApplies ? registrationStreak * 0.025 : 0;
  const unpaidGamesModifier = unpaidGames ? -0.5 * unpaidGames : 0;

  const totalModifier = 1 + streakModifier + reserveModifier + registrationModifier + unpaidGamesModifier;
  // Earned XP never goes below 0; only the no-show penalty can take a player negative.
  const noShowCount = gameHistory.filter(
    (g) => g.status === 'no_show' && !g.excused && g.sequence <= latestSequence && latestSequence - g.sequence < 40,
  ).length;
  const noShowPenalty = noShowCount * NO_SHOW_XP_PENALTY;
  const finalXP = Math.max(0, Math.round(totalBaseXP * totalModifier)) - noShowPenalty;

  const injuryReturnBonusPercent =
    isInjured && injuryReturnStreak && injuryReturnStreak > 0
      ? Math.round(calculateStreakBonus(injuryReturnStreak) * 100)
      : null;

  return {
    baseXP,
    reserveXP,
    reserveCount,
    totalBaseXP,
    effectiveStreak,
    streakModifier,
    streakPercentage,
    reserveModifier,
    registrationModifier,
    unpaidGamesModifier,
    totalModifier,
    noShowCount,
    noShowPenalty,
    finalXP,
    isInjured,
    isShielded,
    injuryReturnBonusPercent,
  };
}

// ─── Per-game detail for the UT XP breakdown ────────────────────────────────

/** How many recent games the strip shows (and the reserve / no-show window). */
export const XP_WINDOW = 40;

/** What a game is worth `gamesAgo` games after it was played. */
export const gameWorth = (gamesAgo: number) => Math.max(1, 20 - gamesAgo * 0.5);

export type XPSlotKind = 'played' | 'reserve' | 'late_reserve' | 'dropped_out' | 'no_show' | 'missed';

export interface XPSlot {
  sequence: number;
  gamesAgo: number;
  /** What playing this game is worth now. */
  worth: number;
  /** What the player actually gets for it. */
  xp: number;
  kind: XPSlotKind;
}

/** A reserve or drop-out registration (these have no team, so gameHistory doesn't carry them). */
export interface XPExtraGame {
  sequence: number;
  status: 'reserve' | 'dropped_out';
  lateReserve?: boolean;
}

/** The last 40 games, oldest first, each with what it earned. */
export function xpSlots(latestSequence: number, gameHistory: XPBreakdownGame[], extra: XPExtraGame[] = []): XPSlot[] {
  const bySeq = new Map<number, XPSlotKind>();
  const rank: Record<XPSlotKind, number> = { played: 5, no_show: 4, reserve: 3, late_reserve: 2, dropped_out: 1, missed: 0 };
  const put = (seq: number, k: XPSlotKind) => {
    const cur = bySeq.get(seq);
    if (!cur || rank[k] > rank[cur]) bySeq.set(seq, k);
  };
  for (const g of gameHistory) {
    if (g.status === 'no_show') put(g.sequence, 'no_show');
    else if (g.status === 'dropped_out') put(g.sequence, 'dropped_out');
    else if (g.status === 'reserve') put(g.sequence, 'reserve');
    else put(g.sequence, 'played');
  }
  for (const e of extra) put(e.sequence, e.status === 'dropped_out' ? 'dropped_out' : e.lateReserve ? 'late_reserve' : 'reserve');

  const out: XPSlot[] = [];
  for (let ago = Math.min(XP_WINDOW, latestSequence) - 1; ago >= 0; ago--) {
    const sequence = latestSequence - ago;
    const kind = bySeq.get(sequence) ?? 'missed';
    const worth = gameWorth(ago);
    out.push({ sequence, gamesAgo: ago, worth, kind, xp: kind === 'played' ? worth : kind === 'reserve' ? 5 : 0 });
  }
  return out;
}

export interface XPBand {
  label: string;
  /** "19.5 down to 15 each" */
  each: string;
  played: number;
  games: number;
  xp: number;
  /** XP if every game in the band had been played. */
  max: number;
}

const BANDS: [string, number, number][] = [
  ['Last game', 0, 0],
  ['1–10 games back', 1, 10],
  ['11–20 back', 11, 20],
  ['21–30 back', 21, 30],
  ['31–39 back', 31, 39],
];

/** Game XP from the strip, grouped by how long ago. */
export function xpBands(slots: XPSlot[]): XPBand[] {
  return BANDS.map(([label, from, to]) => {
    const inBand = slots.filter((s) => s.gamesAgo >= from && s.gamesAgo <= to);
    const played = inBand.filter((s) => s.kind === 'played');
    const hi = gameWorth(from);
    const lo = gameWorth(to);
    return {
      label,
      each: from === to ? `${hi} XP` : `${hi} down to ${lo} each`,
      played: played.length,
      games: inBand.length,
      xp: played.reduce((t, s) => t + s.xp, 0),
      max: inBand.reduce((t, s) => t + s.worth, 0),
    };
  }).filter((b) => b.games > 0);
}

/** Games played more than 40 games ago: 1 XP each, forever. */
export function olderGames(latestSequence: number, gameHistory: XPBreakdownGame[]): number {
  return gameHistory.filter(
    (g) => !['reserve', 'dropped_out', 'no_show'].includes(g.status) && g.sequence <= latestSequence && latestSequence - g.sequence >= XP_WINDOW,
  ).length;
}

/**
 * Roughly what the next game does to a player's XP if they play it or miss it
 * (all else equal). Null while a shield or injury token is in play: those
 * change the streak in ways this can't predict.
 */
export function projectNextGame(input: XPBreakdownInput, extra: XPExtraGame[] = []): { play: number; miss: number } | null {
  if (input.injuryTokenActive || input.shieldActive) return null;
  const now = computeXPBreakdown(input).finalXP;
  const next = input.latestSequence + 1;
  // A reserve 39 games back leaves the 40-game window.
  const leaving = extra.filter((e) => e.status === 'reserve' && !e.lateReserve && next - e.sequence >= XP_WINDOW).length;
  const common = {
    ...input,
    latestSequence: next,
    reserveXP: Math.max(0, (input.reserveXP ?? 0) - leaving * 5),
    benchWarmerStreak: 0,
    registrationStreakApplies: false,
  };
  const play = computeXPBreakdown({
    ...common,
    gameHistory: [...input.gameHistory, { sequence: next, status: 'selected' }],
    currentStreak: input.currentStreak + 1,
  }).finalXP;
  const miss = computeXPBreakdown({ ...common, currentStreak: 0 }).finalXP;
  return { play: play - now, miss: miss - now };
}
